import LogoutIcon from "@mui/icons-material/Logout";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import ReusableTable from "../../common/table/CommonTable";
import {
  fetchAdmissions,
  resetCurrentAdmission,
  updateAdmission,
} from "../../redux/admissions/admissionsActions";

const AdmissionsList = () => {
  const [loading, setLoading] = useState(true);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const admissionsList = useSelector(
    (state) => state?.admission?.fetchAdmissionsAll || {}
  );

  const updateAdmissionState = useSelector(
    (state) => state?.admission?.updateAdmission || {}
  );
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchInputValue, setSearchInputValue] = useState("");
  const [dischargeDialogOpen, setDischargeDialogOpen] = useState(false);
  const [selectedAdmission, setSelectedAdmission] = useState(null);
  const [dischargeType, setDischargeType] = useState(""); 
  const [successSnackbarOpen, setSuccessSnackbarOpen] = useState(false);
  const totalPages = admissionsList?.admissions?.totalPages || 0;
  const [rowsPerPage] = useState(15);
  const searchTimeoutRef = useRef(null);


  const dischargeOptions = [
    { value: "3", label: "Discharged" },
    { value: "4", label: "Death" },
    { value: "5", label: "Abscond" },
    { value: "6", label: "Referral" },
  ];

  const fetchData = async (page) => {
    setLoading(true);
    const payload = {
      pageNo: page,
      pagesize: rowsPerPage,
      pagination_required: true,
      search: searchTerm,
    };

    await dispatch(fetchAdmissions(payload));
    setLoading(false);
  };

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    fetchData(currentPage);
  }, [currentPage, searchTerm, dispatch]);

  useEffect(() => {
    if (
      admissionsList.admissions?.data &&
      Array.isArray(admissionsList.admissions.data)
    ) {
      const formattedData = admissionsList.admissions.data.map((admission) => ({
        id: admission.id,
        admissionNo: admission.admission_no,
        patientId: admission.Patient?.id,
        patientSerialNo: admission.Patient?.patient_serial_no,
        patientName: admission.Patient?.patient_name,
        ipNumber: admission.Patient?.ip_number,
        phone: admission.Patient?.phone,
        admitDate: admission.admit_date,
        dischargeDate: admission.discharge_date,
        blockName: admission.Block?.block_name || "N/A",
        floorName: admission.Floor?.floor_name || "N/A",
        departmentName: admission.Department?.department_name || "N/A",
        wardName: admission.Ward?.ward_name || "N/A",
        status: getStatusLabel(admission.admission_status),
        statusCode: admission.admission_status,
        numAttenders: admission.number_attenders || 0,
        attenders: admission.Attenders || [],
      }));

      setData(formattedData);

      const lowercasedSearchTerm = searchTerm?.toLowerCase() || "";
      const filtered = searchTerm
        ? formattedData.filter(
            (admission) =>
              admission.patientName
                .toLowerCase()
                .includes(lowercasedSearchTerm) ||
              admission.admissionNo
                .toLowerCase()
                .includes(lowercasedSearchTerm) ||
              admission.patientSerialNo
                .toLowerCase()
                .includes(lowercasedSearchTerm) ||
              admission.blockName
                .toLowerCase()
                .includes(lowercasedSearchTerm) ||
              admission.floorName
                .toLowerCase()
                .includes(lowercasedSearchTerm) ||
              admission.wardName.toLowerCase().includes(lowercasedSearchTerm) ||
              admission.departmentName
                .toLowerCase()
                .includes(lowercasedSearchTerm)
          )
        : formattedData;

      setFilteredData(filtered);
    } else {
      setData([]);
      setFilteredData([]);
    }
  }, [admissionsList, searchTerm]);

  useEffect(() => {
    if (updateAdmissionState.success) {
      setSuccessSnackbarOpen(true);
      dispatch(resetCurrentAdmission());
      fetchData(currentPage);
    }
  }, [updateAdmissionState.success, dispatch]);

  const getStatusLabel = (statusCode) => {
    switch (statusCode) {
      case 1:
        return "Active";
      case 2:
        return "In Active";
      case 3:
        return "Discharged";
        case 4:
        return "Death";
        case 5:
        return "Abscond";
        case 6:
        return "Referral";
      default:
        return "Unknown";
    }
  };

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchInputValue(value);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      setSearchTerm(value);
      setCurrentPage(1);
    }, 1000);
  };

  const handleViewClick = (row) => {
    navigate(`/admission/view/${row.patientId}`);
  };

  const handleDischargeClick = (row) => {
    if (row.statusCode === 1) {
      setSelectedAdmission(row);
      setDischargeType("");
      setDischargeDialogOpen(true);
    }
  };

  const handleDischargeConfirm = () => {
    if (selectedAdmission && dischargeType) {
      const currentDate = new Date();
      const formattedDate = `${currentDate.getFullYear()}-${String(
        currentDate.getMonth() + 1
      ).padStart(2, "0")}-${String(currentDate.getDate()).padStart(
        2,
        "0"
      )} ${String(currentDate.getHours()).padStart(2, "0")}:${String(
        currentDate.getMinutes()
      ).padStart(2, "0")}`;
      
      const formdata = {
        admission_status: dischargeType,
        discharge_date: formattedDate,
      };

      dispatch(updateAdmission(selectedAdmission.id, formdata));
      setDischargeDialogOpen(false);
      setDischargeType(""); 
      setSelectedAdmission(null);
    }
  };

  const handleDischargeCancel = () => {
    setDischargeDialogOpen(false);
    setSelectedAdmission(null);
    setDischargeType(""); 
  };

  const handleDischargeTypeChange = (event) => {
    setDischargeType(event.target.value);
  };

  const handleCloseSuccessSnackbar = () => {
    setSuccessSnackbarOpen(false);
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };


  const getDischargeTypeLabel = (value) => {
    const option = dischargeOptions.find(opt => opt.value === value);
    return option ? option.label : "Updated";
  };

  const columns = [
    {
      id: "admissionNo",
      label: "Admission No",
      sortable: true,
      render: (value, row) => (
        <Typography
          component="span"
          sx={{
            cursor: "pointer",
            color: "dodgerblue",
            "&:hover": {
              textDecoration: "underline",
            },
          }}
          onClick={() => handleViewClick(row)}
        >
          {value}
        </Typography>
      ),
    },
    { id: "patientSerialNo", label: "Patient ID", sortable: true },
    { id: "patientName", label: "Patient Name", sortable: true },
    { id: "ipNumber", label: "IP Number", sortable: true },
    { id: "phone", label: "Phone", sortable: true },
    {
      id: "admitDate",
      label: "Admit Date",
      sortable: true,
      render: (value) => {
        const date = new Date(value);
        return isNaN(date) ? "Invalid Date" : date.toLocaleDateString();
      },
    },
    {
      id: "dischargeDate",
      label: "Discharge Date",
      sortable: true,
      render: (value) => {
        if (!value) return "Not Discharged";
        const date = new Date(value);
        return isNaN(date) ? "Invalid Date" : date.toLocaleDateString();
      },
    },
    { id: "blockName", label: "Block", sortable: true },
    { id: "floorName", label: "Floor", sortable: true },
    { id: "departmentName", label: "Department", sortable: true },
    { id: "wardName", label: "Ward", sortable: true },
    { id: "numAttenders", label: "Attenders", sortable: true },
    {
      id: "status",
      label: "Status",
      sortable: true,
      render: (value) => (
        <Chip
          label={value}
          color={
            value === "Active"
              ? "success"
              : value === "In Active"
              ? "default"
              : value === "Discharged"
              ? "primary"
              : "warning"
          }
        />
      ),
    },
    {
      id: "actions",
      label: "Actions",
      sortable: false,
      render: (_, row) => (
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 3 }}>
          {row.statusCode !== 3 && (
            <Button
              color="blue"
              onClick={() => handleViewClick(row)}
              size="small"
              title="View"
              startIcon={
                <VisibilityIcon
                  sx={{
                    color: "dodgerblue",
                  }}
                />
              }
              sx={{
                color: "dodgerblue",
              }}
            >
              View
            </Button>
          )}

          {row.statusCode === 1 && (
            <Button
              onClick={() => handleDischargeClick(row)}
              size="small"
              title="Discharge"
              startIcon={
                <LogoutIcon
                  sx={{
                    color: "red",
                  }}
                />
              }
              sx={{
                color: "red",
              }}
            >
              Discharge
            </Button>
          )}
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Search Admissions
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="search by Patient Name"
                    value={searchInputValue}
                    onChange={handleSearchChange}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                    size="small"
                  />
                </Grid>
              </Grid>
            </Box>
          </Grid>
        </Grid>
        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid
            item
            xs={12}
            sx={{ display: "flex", justifyContent: "flex-end" }}
          >
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate("/admission/create")}
            >
              Add Admission
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <ReusableTable
        title="Admissions"
        columns={columns}
        data={filteredData}
        isLoading={loading}
        headerColor="#000"
        rowColor="#ffffff"
        rowsPerPage={rowsPerPage}
        alternateRowColor="#f5f5f5"
        withPagination={true}
        withActions={false}
        showDownloadOption={true}
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        emptyMessage="No records found"
      />

      <Dialog
        open={dischargeDialogOpen}
        onClose={handleDischargeCancel}
        aria-labelledby="discharge-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          id="discharge-dialog-title"
          sx={{ bgcolor: "red", color: "white" }}
        >
          Discharge Patient
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography sx={{ mb: 3 }}>
            Please select discharge type for patient:
            {selectedAdmission && (
              <Box component="span" fontWeight="bold">
                {" "}
                {selectedAdmission.patientName}
              </Box>
            )}
          </Typography>
          
          <FormControl fullWidth variant="outlined">
            <InputLabel id="discharge-type-label">Discharge Type</InputLabel>
            <Select
              labelId="discharge-type-label"
              id="discharge-type-select"
              value={dischargeType}
              onChange={handleDischargeTypeChange}
              label="Discharge Type"
            >
              {dischargeOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={handleDischargeCancel}
            sx={{
              backgroundColor: "orange",
              color: "white",
              "&:hover": {
                backgroundColor: "darkorange",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDischargeConfirm}
            variant="contained"
            disabled={!dischargeType} 
            sx={{
              backgroundColor: dischargeType ? "green" : "grey",
              color: "white",
              "&:hover": {
                backgroundColor: dischargeType ? "darkgreen" : "grey",
              },
            }}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={successSnackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSuccessSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSuccessSnackbar}
          severity="success"
          sx={{ width: "100%", bgcolor: "green", color: "white" }}
        >
          Patient {getDischargeTypeLabel(dischargeType)} successfully!
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdmissionsList;